import { useEffect, useState } from 'react';
import { customerPortalAPI, productsAPI } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  Upload, FileImage, FileVideo, File, Eye, Trash2, CheckCircle, Clock, 
  AlertCircle, Star, MessageSquare, Users, TrendingUp, Image, Video,
  ThumbsUp, Award, Quote, Filter, RefreshCw, Download, Loader2
} from 'lucide-react';

const statusColors = {
  pending: 'bg-amber-100 text-amber-700',
  reviewed: 'bg-blue-100 text-blue-700',
  resolved: 'bg-emerald-100 text-emerald-700',
  archived: 'bg-slate-100 text-slate-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
  featured: 'bg-purple-100 text-purple-700'
};

const categoryColors = {
  issue: 'bg-red-100 text-red-700',
  success: 'bg-emerald-100 text-emerald-700',
  maintenance: 'bg-blue-100 text-blue-700',
  general: 'bg-slate-100 text-slate-700',
  other: 'bg-slate-100 text-slate-700'
};

export const CustomerPortalAdminPage = () => {
  const [activeTab, setActiveTab] = useState('files');
  const [files, setFiles] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [filesStats, setFilesStats] = useState({ total: 0, pending: 0, reviewed: 0 });
  const [testimonialsStats, setTestimonialsStats] = useState({ total: 0, pending: 0, approved: 0, featured: 0 });
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [fileStatusFilter, setFileStatusFilter] = useState('all');
  const [fileCategoryFilter, setFileCategoryFilter] = useState('all');
  const [testimonialStatusFilter, setTestimonialStatusFilter] = useState('all');
  
  // View/Edit dialogs
  const [viewFile, setViewFile] = useState(null);
  const [viewTestimonial, setViewTestimonial] = useState(null);
  const [editNotes, setEditNotes] = useState('');

  useEffect(() => {
    if (activeTab === 'files') {
      fetchFiles();
    } else {
      fetchTestimonials();
    }
  }, [activeTab, fileStatusFilter, fileCategoryFilter, testimonialStatusFilter]);

  const fetchFiles = async () => {
    try {
      setLoading(true);
      const params = {};
      if (fileStatusFilter !== 'all') params.status = fileStatusFilter;
      if (fileCategoryFilter !== 'all') params.category = fileCategoryFilter;
      
      const res = await customerPortalAPI.adminGetAllFiles(params);
      setFiles(res.data.files || []);
      setFilesStats(res.data.stats || { total: 0, pending: 0, reviewed: 0 });
    } catch (error) {
      toast.error('Failed to fetch files');
    } finally {
      setLoading(false);
    }
  };

  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      const params = {};
      if (testimonialStatusFilter !== 'all') params.status = testimonialStatusFilter;
      
      const res = await customerPortalAPI.adminGetAllTestimonials(params);
      setTestimonials(res.data.testimonials || []);
      setTestimonialsStats(res.data.stats || { total: 0, pending: 0, approved: 0, featured: 0 });
    } catch (error) {
      toast.error('Failed to fetch testimonials');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateFileStatus = async (fileId, status) => {
    try {
      await customerPortalAPI.adminUpdateFile(fileId, { status, admin_notes: editNotes || undefined });
      toast.success('File status updated');
      setViewFile(null);
      setEditNotes('');
      fetchFiles();
    } catch (error) {
      toast.error('Failed to update file');
    }
  };

  const openFileView = async (file) => {
    // Open immediately with metadata, then load the actual image/video content
    setViewFile(file);
    setEditNotes('');
    try {
      const res = await customerPortalAPI.adminGetFile(file.id);
      setViewFile(res.data);
    } catch (error) {
      toast.error('Failed to load file content');
    }
  };

  const handleUpdateTestimonial = async (testimonialId, updates) => {
    try {
      await customerPortalAPI.adminUpdateTestimonial(testimonialId, updates);
      toast.success('Testimonial updated');
      setViewTestimonial(null);
      setEditNotes('');
      fetchTestimonials();
    } catch (error) {
      toast.error('Failed to update testimonial');
    }
  };

  const getFileIcon = (fileType) => {
    switch (fileType) {
      case 'image': return <FileImage className="h-5 w-5 text-blue-600" />;
      case 'video': return <FileVideo className="h-5 w-5 text-purple-600" />;
      default: return <File className="h-5 w-5 text-slate-600" />;
    }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${i < rating ? 'text-yellow-500 fill-yellow-500' : 'text-slate-300'}`}
      />
    ));
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="customer-portal-admin-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] heading-tight">Customer Portal</h1>
          <p className="text-muted-foreground mt-1">Manage customer uploads and testimonials</p>
        </div>
        <Button variant="outline" onClick={() => activeTab === 'files' ? fetchFiles() : fetchTestimonials()}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Upload className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Files</p>
                <p className="text-2xl font-bold">{filesStats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pending Review</p>
                <p className="text-2xl font-bold">{filesStats.pending}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <MessageSquare className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Testimonials</p>
                <p className="text-2xl font-bold">{testimonialsStats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Award className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Featured</p>
                <p className="text-2xl font-bold">{testimonialsStats.featured}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="files" data-testid="files-tab">
            <Upload className="h-4 w-4 mr-2" />
            Customer Files
          </TabsTrigger>
          <TabsTrigger value="testimonials" data-testid="testimonials-tab">
            <Quote className="h-4 w-4 mr-2" />
            Testimonials
          </TabsTrigger>
        </TabsList>

        {/* Files Tab */}
        <TabsContent value="files" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-muted-foreground" />
                  <Select value={fileStatusFilter} onValueChange={setFileStatusFilter}>
                    <SelectTrigger className="w-[150px]">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="reviewed">Reviewed</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                      <SelectItem value="archived">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Select value={fileCategoryFilter} onValueChange={setFileCategoryFilter}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    <SelectItem value="issue">Issue</SelectItem>
                    <SelectItem value="success">Success</SelectItem>
                    <SelectItem value="maintenance">Maintenance</SelectItem>
                    <SelectItem value="general">General</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Files Table */}
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>File</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Uploaded</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-900 mx-auto"></div>
                      </TableCell>
                    </TableRow>
                  ) : files.length > 0 ? (
                    files.map((file) => (
                      <TableRow key={file.id} data-testid={`file-row-${file.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            {getFileIcon(file.file_type)}
                            <div>
                              <p className="font-medium">{file.title}</p>
                              <p className="text-xs text-muted-foreground">{file.file_name}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{file.contact_name}</p>
                            <p className="text-xs text-muted-foreground">{file.contact_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={categoryColors[file.category] || categoryColors.general}>
                            {file.category}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={statusColors[file.status] || statusColors.pending}>
                            {file.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {format(new Date(file.created_at), 'MMM d, yyyy')}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openFileView(file)}
                            data-testid={`view-file-${file.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        No files found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Testimonials Tab */}
        <TabsContent value="testimonials" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-muted-foreground" />
                  <Select value={testimonialStatusFilter} onValueChange={setTestimonialStatusFilter}>
                    <SelectTrigger className="w-[150px]">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Testimonials Grid */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {loading ? (
              <div className="col-span-full text-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-900 mx-auto"></div>
              </div>
            ) : testimonials.length > 0 ? (
              testimonials.map((testimonial) => (
                <Card key={testimonial.id} className="overflow-hidden" data-testid={`testimonial-card-${testimonial.id}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg">{testimonial.contact_name}</CardTitle>
                        {testimonial.company_name && (
                          <CardDescription>{testimonial.company_name}</CardDescription>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <Badge className={statusColors[testimonial.status] || statusColors.pending}>
                          {testimonial.status}
                        </Badge>
                        {testimonial.featured && (
                          <Badge className="bg-purple-100 text-purple-700">
                            <Award className="h-3 w-3 mr-1" />
                            Featured
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-0.5 mt-2">
                      {renderStars(testimonial.rating)}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <h4 className="font-medium mb-2">{testimonial.title}</h4>
                    <p className="text-sm text-muted-foreground line-clamp-3">{testimonial.content}</p>
                    <div className="mt-4 flex justify-between items-center">
                      <span className="text-xs text-muted-foreground">
                        {format(new Date(testimonial.created_at), 'MMM d, yyyy')}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setViewTestimonial(testimonial)}
                        data-testid={`view-testimonial-${testimonial.id}`}
                      >
                        <Eye className="h-3 w-3 mr-1" />
                        Review
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <div className="col-span-full text-center py-8 text-muted-foreground">
                No testimonials found
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* View File Dialog */}
      <Dialog open={!!viewFile} onOpenChange={() => { setViewFile(null); setEditNotes(''); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {viewFile && getFileIcon(viewFile.file_type)}
              {viewFile?.title}
            </DialogTitle>
            <DialogDescription>
              Uploaded by {viewFile?.contact_name} ({viewFile?.contact_email})
            </DialogDescription>
          </DialogHeader>
          {viewFile && (
            <div className="space-y-4">
              {/* Actual photo / video preview */}
              <div className="flex items-center justify-center bg-slate-100 rounded-lg overflow-hidden min-h-[160px]">
                {viewFile.file_url ? (
                  viewFile.file_type === 'image' ? (
                    <img src={viewFile.file_url} alt={viewFile.title} className="max-h-[55vh] w-auto object-contain" data-testid="admin-file-image" />
                  ) : viewFile.file_type === 'video' ? (
                    <video src={viewFile.file_url} controls className="max-h-[55vh] w-full" data-testid="admin-file-video" />
                  ) : (
                    <a href={viewFile.file_url} download={viewFile.file_name} className="text-blue-600 underline p-8">
                      Download {viewFile.file_name}
                    </a>
                  )
                ) : (
                  <div className="flex items-center gap-2 text-muted-foreground p-8">
                    <Loader2 className="h-5 w-5 animate-spin" /> Loading file…
                  </div>
                )}
              </div>
              {viewFile.file_url && (
                <a href={viewFile.file_url} download={viewFile.file_name} className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline" data-testid="admin-file-download">
                  Download original
                </a>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Category</Label>
                  <Badge className={categoryColors[viewFile.category] || categoryColors.general}>
                    {viewFile.category}
                  </Badge>
                </div>
                <div>
                  <Label>Status</Label>
                  <Badge className={statusColors[viewFile.status] || statusColors.pending}>
                    {viewFile.status}
                  </Badge>
                </div>
                <div>
                  <Label>File Name</Label>
                  <p className="text-sm">{viewFile.file_name}</p>
                </div>
                <div>
                  <Label>File Size</Label>
                  <p className="text-sm">{(viewFile.file_size / 1024).toFixed(1)} KB</p>
                </div>
              </div>
              
              {viewFile.description && (
                <div>
                  <Label>Description</Label>
                  <p className="text-sm text-muted-foreground">{viewFile.description}</p>
                </div>
              )}
              
              <div>
                <Label>Admin Notes</Label>
                <Textarea
                  value={editNotes || viewFile.admin_notes || ''}
                  onChange={(e) => setEditNotes(e.target.value)}
                  placeholder="Add notes about this file..."
                  rows={3}
                />
              </div>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => handleUpdateFileStatus(viewFile?.id, 'reviewed')}
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Mark Reviewed
            </Button>
            <Button
              variant="outline"
              onClick={() => handleUpdateFileStatus(viewFile?.id, 'resolved')}
              className="text-emerald-600"
            >
              <ThumbsUp className="h-4 w-4 mr-2" />
              Mark Resolved
            </Button>
            <Button
              variant="outline"
              onClick={() => handleUpdateFileStatus(viewFile?.id, 'archived')}
              className="text-slate-600"
            >
              Archive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Testimonial Dialog */}
      <Dialog open={!!viewTestimonial} onOpenChange={() => { setViewTestimonial(null); setEditNotes(''); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{viewTestimonial?.title}</DialogTitle>
            <DialogDescription>
              From {viewTestimonial?.contact_name}
              {viewTestimonial?.company_name && ` at ${viewTestimonial?.company_name}`}
            </DialogDescription>
          </DialogHeader>
          {viewTestimonial && (
            <div className="space-y-4">
              <div className="flex gap-1">
                {renderStars(viewTestimonial.rating)}
              </div>
              
              <div className="bg-slate-50 p-4 rounded-lg">
                <Quote className="h-6 w-6 text-slate-300 mb-2" />
                <p className="text-sm italic">{viewTestimonial.content}</p>
              </div>
              
              {viewTestimonial.use_case && (
                <div>
                  <Label>Use Case</Label>
                  <p className="text-sm text-muted-foreground">{viewTestimonial.use_case}</p>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Allow Public Display</Label>
                  <p className="text-sm">{viewTestimonial.allow_public ? 'Yes' : 'No'}</p>
                </div>
                <div>
                  <Label>Allow Photo</Label>
                  <p className="text-sm">{viewTestimonial.allow_photo ? 'Yes' : 'No'}</p>
                </div>
              </div>
              
              <div>
                <Label>Admin Notes</Label>
                <Textarea
                  value={editNotes || viewTestimonial.admin_notes || ''}
                  onChange={(e) => setEditNotes(e.target.value)}
                  placeholder="Add notes about this testimonial..."
                  rows={3}
                />
              </div>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => handleUpdateTestimonial(viewTestimonial?.id, { 
                status: 'approved',
                admin_notes: editNotes || undefined
              })}
              className="text-emerald-600"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Approve
            </Button>
            <Button
              variant="outline"
              onClick={() => handleUpdateTestimonial(viewTestimonial?.id, { 
                status: 'approved',
                featured: true,
                admin_notes: editNotes || undefined
              })}
              className="text-purple-600"
            >
              <Award className="h-4 w-4 mr-2" />
              Feature
            </Button>
            <Button
              variant="outline"
              onClick={() => handleUpdateTestimonial(viewTestimonial?.id, { 
                status: 'rejected',
                admin_notes: editNotes || undefined
              })}
              className="text-red-600"
            >
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerPortalAdminPage;
